/**
 * Greek cities, towns, and popular tourist villages. Used to populate
 * `<datalist>` autocomplete and the LocationPicker dropdown so hosts type
 * the first letter and pick from a curated list — keeps `Listing.city`
 * spellings consistent so search filters and city tiles aggregate.
 *
 * Each entry maps a city to its rough geographic region (used for the
 * "Also try nearby" hint on the search page). Sort alphabetically when
 * adding entries.
 *
 * ~250 places covering all 13 Greek regional units' main settlements
 * plus every island that hosts visitors. Not exhaustive — Nominatim
 * catches anything missing here.
 */
export type Region =
  | "Attica"
  | "Central Greece"
  | "Macedonia"
  | "Thessaly"
  | "Epirus"
  | "Western Greece"
  | "Peloponnese"
  | "Crete"
  | "Cyclades"
  | "Dodecanese"
  | "Ionian"
  | "North Aegean"
  | "Saronic"
  | "Sporades"
  | "Thrace";

export const GREEK_CITY_REGIONS: Record<string, Region> = {
  // ─── Attica ───
  Athens: "Attica",
  Piraeus: "Attica",
  Glyfada: "Attica",
  Voula: "Attica",
  Vouliagmeni: "Attica",
  Varkiza: "Attica",
  Saronida: "Attica",
  Anavyssos: "Attica",
  "Palaia Fokaia": "Attica",
  Lavrio: "Attica",
  Sounio: "Attica",
  Lagonisi: "Attica",
  Markopoulo: "Attica",
  "Porto Rafti": "Attica",
  Rafina: "Attica",
  "Nea Makri": "Attica",
  Marathon: "Attica",
  Schinias: "Attica",
  "Agios Andreas": "Attica",
  Kalyvia: "Attica",
  Spata: "Attica",
  Paiania: "Attica",
  Kifisia: "Attica",
  "Nea Erythraia": "Attica",
  Filothei: "Attica",
  Psychiko: "Attica",
  Marousi: "Attica",
  Chalandri: "Attica",
  "Nea Smyrni": "Attica",
  "Palaio Faliro": "Attica",
  Alimos: "Attica",
  Elliniko: "Attica",
  Megara: "Attica",
  Salamina: "Attica",
  // ─── Saronic islands ───
  Aegina: "Saronic",
  Agistri: "Saronic",
  Poros: "Saronic",
  Hydra: "Saronic",
  Spetses: "Saronic",
  Methana: "Saronic",
  // ─── Central Greece (Sterea Ellada) ───
  Chalkida: "Central Greece",
  Eretria: "Central Greece",
  Karystos: "Central Greece",
  Kymi: "Central Greece",
  Edipsos: "Central Greece",
  Lamia: "Central Greece",
  Karpenisi: "Central Greece",
  Arachova: "Central Greece",
  Delphi: "Central Greece",
  Itea: "Central Greece",
  Galaxidi: "Central Greece",
  Levadia: "Central Greece",
  Thebes: "Central Greece",
  "Agios Konstantinos": "Central Greece",
  // ─── Thessaly ───
  Larissa: "Thessaly",
  Volos: "Thessaly",
  Trikala: "Thessaly",
  Karditsa: "Thessaly",
  Pelion: "Thessaly",
  Tsagarada: "Thessaly",
  "Agios Ioannis Pelion": "Thessaly",
  Damouchari: "Thessaly",
  Portaria: "Thessaly",
  Meteora: "Thessaly",
  Kalambaka: "Thessaly",
  Kastraki: "Thessaly",
  // ─── Sporades ───
  Skiathos: "Sporades",
  Skopelos: "Sporades",
  Alonissos: "Sporades",
  Skyros: "Sporades",
  // ─── Epirus ───
  Ioannina: "Epirus",
  Metsovo: "Epirus",
  Igoumenitsa: "Epirus",
  Parga: "Epirus",
  Preveza: "Epirus",
  Sivota: "Epirus",
  Konitsa: "Epirus",
  Zagori: "Epirus",
  Papingo: "Epirus",
  Arta: "Epirus",
  // ─── Western Greece ───
  Patras: "Western Greece",
  Pyrgos: "Western Greece",
  Killini: "Western Greece",
  Olympia: "Western Greece",
  Mesolongi: "Western Greece",
  Agrinio: "Western Greece",
  Lefkimmi: "Western Greece",
  Nafpaktos: "Western Greece",
  // ─── Macedonia ───
  Thessaloniki: "Macedonia",
  Halkidiki: "Macedonia",
  Kassandra: "Macedonia",
  Sithonia: "Macedonia",
  "Nea Moudania": "Macedonia",
  Hanioti: "Macedonia",
  Pefkochori: "Macedonia",
  Sarti: "Macedonia",
  Ouranoupoli: "Macedonia",
  Vergina: "Macedonia",
  Veria: "Macedonia",
  "Naoussa, Imathia": "Macedonia",
  Edessa: "Macedonia",
  Pella: "Macedonia",
  Kilkis: "Macedonia",
  Serres: "Macedonia",
  Drama: "Macedonia",
  Kavala: "Macedonia",
  Thasos: "Macedonia",
  Kozani: "Macedonia",
  Grevena: "Macedonia",
  Kastoria: "Macedonia",
  Florina: "Macedonia",
  Prespa: "Macedonia",
  Katerini: "Macedonia",
  Litochoro: "Macedonia",
  "Paralia Katerinis": "Macedonia",
  // ─── Thrace ───
  Komotini: "Thrace",
  Xanthi: "Thrace",
  Alexandroupoli: "Thrace",
  Orestiada: "Thrace",
  Samothraki: "Thrace",
  // ─── Peloponnese ───
  Corinth: "Peloponnese",
  Loutraki: "Peloponnese",
  Xylokastro: "Peloponnese",
  Diakopto: "Peloponnese",
  Kalavryta: "Peloponnese",
  Nafplio: "Peloponnese",
  Tolo: "Peloponnese",
  Argos: "Peloponnese",
  Mycenae: "Peloponnese",
  Tripoli: "Peloponnese",
  Sparta: "Peloponnese",
  Mystras: "Peloponnese",
  Gythio: "Peloponnese",
  Areopoli: "Peloponnese",
  Limeni: "Peloponnese",
  Monemvasia: "Peloponnese",
  Neapoli: "Peloponnese",
  Elafonisos: "Peloponnese",
  Kalamata: "Peloponnese",
  Pylos: "Peloponnese",
  Methoni: "Peloponnese",
  Koroni: "Peloponnese",
  Stoupa: "Peloponnese",
  Kardamyli: "Peloponnese",
  Kyparissia: "Peloponnese",
  // ─── Crete ───
  Heraklion: "Crete",
  "Kokkini Hani": "Crete",
  Hersonissos: "Crete",
  Malia: "Crete",
  Stalida: "Crete",
  "Agios Nikolaos": "Crete",
  Elounda: "Crete",
  "Plaka, Lasithi": "Crete",
  Sitia: "Crete",
  Ierapetra: "Crete",
  Makrigialos: "Crete",
  Rethymno: "Crete",
  Plakias: "Crete",
  "Agia Galini": "Crete",
  Bali: "Crete",
  Chania: "Crete",
  Stalos: "Crete",
  "Agia Marina, Chania": "Crete",
  Platanias: "Crete",
  Kissamos: "Crete",
  Falassarna: "Crete",
  Elafonisi: "Crete",
  Paleochora: "Crete",
  Sougia: "Crete",
  Loutro: "Crete",
  Sfakia: "Crete",
  Anogia: "Crete",
  Archanes: "Crete",
  Matala: "Crete",
  // ─── Cyclades ───
  Mykonos: "Cyclades",
  "Ano Mera": "Cyclades",
  Santorini: "Cyclades",
  Oia: "Cyclades",
  Fira: "Cyclades",
  Imerovigli: "Cyclades",
  Kamari: "Cyclades",
  Perissa: "Cyclades",
  "Akrotiri, Santorini": "Cyclades",
  Naxos: "Cyclades",
  "Naxos Town": "Cyclades",
  "Agios Prokopios": "Cyclades",
  "Agia Anna, Naxos": "Cyclades",
  Plaka: "Cyclades",
  Filoti: "Cyclades",
  Apollonas: "Cyclades",
  Paros: "Cyclades",
  Parikia: "Cyclades",
  Naoussa: "Cyclades",
  "Aliki, Paros": "Cyclades",
  Antiparos: "Cyclades",
  Ios: "Cyclades",
  Mylopotas: "Cyclades",
  Folegandros: "Cyclades",
  Sikinos: "Cyclades",
  Anafi: "Cyclades",
  Amorgos: "Cyclades",
  Aegiali: "Cyclades",
  Katapola: "Cyclades",
  Koufonisia: "Cyclades",
  Donousa: "Cyclades",
  Iraklia: "Cyclades",
  Schinoussa: "Cyclades",
  Andros: "Cyclades",
  Batsi: "Cyclades",
  Korthi: "Cyclades",
  Tinos: "Cyclades",
  "Pyrgos, Tinos": "Cyclades",
  Syros: "Cyclades",
  Ermoupoli: "Cyclades",
  Galissas: "Cyclades",
  Kea: "Cyclades",
  Kythnos: "Cyclades",
  Serifos: "Cyclades",
  Sifnos: "Cyclades",
  Kimolos: "Cyclades",
  Milos: "Cyclades",
  Pollonia: "Cyclades",
  Adamantas: "Cyclades",
  // ─── Dodecanese ───
  Rhodes: "Dodecanese",
  Lindos: "Dodecanese",
  Pefkos: "Dodecanese",
  Kolymbia: "Dodecanese",
  Faliraki: "Dodecanese",
  Ialysos: "Dodecanese",
  Archangelos: "Dodecanese",
  Kos: "Dodecanese",
  "Kos Town": "Dodecanese",
  Kardamena: "Dodecanese",
  Tigaki: "Dodecanese",
  Kefalos: "Dodecanese",
  Mastichari: "Dodecanese",
  Patmos: "Dodecanese",
  Skala: "Dodecanese",
  Leros: "Dodecanese",
  Kalymnos: "Dodecanese",
  Symi: "Dodecanese",
  Tilos: "Dodecanese",
  Nisyros: "Dodecanese",
  Astypalaia: "Dodecanese",
  Karpathos: "Dodecanese",
  Kasos: "Dodecanese",
  Kastellorizo: "Dodecanese",
  // ─── Ionian ───
  Corfu: "Ionian",
  "Corfu Town": "Ionian",
  Paleokastritsa: "Ionian",
  Kavos: "Ionian",
  Sidari: "Ionian",
  Acharavi: "Ionian",
  Kassiopi: "Ionian",
  Roda: "Ionian",
  Paxos: "Ionian",
  Antipaxos: "Ionian",
  Lefkada: "Ionian",
  Nidri: "Ionian",
  Vasiliki: "Ionian",
  "Agios Nikitas": "Ionian",
  Cephalonia: "Ionian",
  Argostoli: "Ionian",
  Lassi: "Ionian",
  "Skala, Cephalonia": "Ionian",
  Fiscardo: "Ionian",
  Sami: "Ionian",
  Ithaca: "Ionian",
  "Vathy, Ithaca": "Ionian",
  Zakynthos: "Ionian",
  "Zakynthos Town": "Ionian",
  Tsilivi: "Ionian",
  Laganas: "Ionian",
  Argassi: "Ionian",
  Kythira: "Ionian",
  // ─── North Aegean ───
  Lesbos: "North Aegean",
  Mytilene: "North Aegean",
  Molyvos: "North Aegean",
  Eressos: "North Aegean",
  Plomari: "North Aegean",
  Chios: "North Aegean",
  "Chios Town": "North Aegean",
  Mesta: "North Aegean",
  Pyrgi: "North Aegean",
  Samos: "North Aegean",
  "Vathy, Samos": "North Aegean",
  Pythagoreio: "North Aegean",
  Karlovasi: "North Aegean",
  Ikaria: "North Aegean",
  "Agios Kirykos": "North Aegean",
  Fourni: "North Aegean",
  Lemnos: "North Aegean",
  Myrina: "North Aegean",
  "Agios Efstratios": "North Aegean",
};

export const GREEK_CITIES = Object.keys(GREEK_CITY_REGIONS);
export const GREEK_CITIES_SORTED = [...GREEK_CITIES].sort((a, b) => a.localeCompare(b));

/**
 * Other cities in the same region as `city`, ordered alphabetically.
 * Empty array if city isn't in our table. Used by /search to suggest
 * alternative destinations when the queried city has few/no results.
 */
export function nearbyCities(city: string, limit = 8): string[] {
  const region = GREEK_CITY_REGIONS[city];
  if (!region) return [];
  return GREEK_CITIES_SORTED.filter(
    (c) => c !== city && GREEK_CITY_REGIONS[c] === region,
  ).slice(0, limit);
}
